import {
	HubDataSource,
	HubItemCategoryModel,
	HubItemDataSource,
	HubOverviewModel,
	listItems
} from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	BusyIndicator,
	ContextActionBar,
	FloatingActionListMenu,
	NoResults,
	PageContentTitle,
	ToolbarAction,
	ToolbarActionGroup
} from "client/Components";
import {
	MediaQueryKind,
	useForceRerender,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ActionKind,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { ActiveFilter, HubViewChangeHandler } from "../common";
import { AnnouncementSection } from "./AnnouncementSection";
import { EventSection } from "./EventSection";
import { KnowledgeItemSection } from "./KnowledgeItemSection";
import styles from "./Overview.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { useHubManagementDialog } from "../Management/HubManagementDialog/HubManagementDialog";
import { useCreateHubItemDialog } from "../CreateHubItemDialog/CreateHubItemDialog";
import { useHubItemsChangedListener } from "../useHubItemsChangedListener";
import { CommunityPostsSection } from "./CommunityPostsSection";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { CommunityCreationPermissions } from "../HubLayout";
import { SaveCommunityResult, useEditCommunityDialog } from "../Communities/EditCommunityDialog/EditCommunityDialog";
import { useCreateCommunityPostDialog } from "../Communities/Composer/CreateCommunityPostDialog";
import { CommunityPostDetailsViewModel } from "client/Api/Communities/communityPostViewer";

export type OverviewProps = {
	categories: readonly HubItemCategoryModel[];
	hubItemKindsCanCreateNewCategoriesFor: readonly HubItemKind[];
	canOpenHubManagement: boolean;
	communityCreationPermissions: CommunityCreationPermissions;
	onViewChange: HubViewChangeHandler;
	onActiveFilterChange: (activeFilter: ActiveFilter | null) => void;
	onSelectCategory: (source: HubItemDataSource, categoryId: number | null) => void;
};

export const Overview = (props: OverviewProps): ReactElement => {
	const {
		categories,
		hubItemKindsCanCreateNewCategoriesFor,
		canOpenHubManagement,
		communityCreationPermissions,
		onViewChange,
		onActiveFilterChange,
		onSelectCategory
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const [reload, loadVersion] = useForceRerender();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const userTimezone: TimeZone = useUserTimezone();

	const [hubManagementDialog, openHubManagementDialog] = useHubManagementDialog();
	const [createHubItemDialog, openCreateHubItemDialog] = useCreateHubItemDialog();
	const [editCommunityDialog, openEditCommunityDialog] = useEditCommunityDialog();
	const [createCommunityPostDialog, openCreateCommunityPostDialog] = useCreateCommunityPostDialog();

	useHubItemsChangedListener(reload);

	const getCategory = useCallback(
		(categoryId: number | nil): HubItemCategoryModel | undefined => {
			if (categories === null || isNil(categoryId)) {
				return undefined;
			}

			return categories.find(c => c.id === categoryId);
		},
		[categories]
	);

	const createHubItemHandler = useCallback(
		async (kind: HubItemKind): Promise<void> => {

			const outcome: TryResult<void> = await openCreateHubItemDialog({
				kind
			});

			if (outcome.wasSuccessful) {
				reload();
			}
		},
		[
			openCreateHubItemDialog,
			reload
		]
	);

	const manageHandler = useCallback(
		(): void => {
			openHubManagementDialog({});
		},
		[openHubManagementDialog]
	);

	const createCommunityHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<SaveCommunityResult> = await openEditCommunityDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			onViewChange({
				source: HubDataSource.Community,
				kind: "community",
				id: result.result
					.id
			});
		},
		[
			onViewChange,
			openEditCommunityDialog
		]
	);

	const createCommunityPostHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<CommunityPostDetailsViewModel> = await openCreateCommunityPostDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			onViewChange({
				source: HubDataSource.Community,
				kind: "community",
				id: result
					.result
					.community
					.id
			});
		},
		[
			onViewChange,
			openCreateCommunityPostDialog
		]
	);

	const mainActions = useMemo(
		(): readonly OverviewAction[] => {
			return getMainActions({
				createHubItem: createHubItemHandler,
				createCommunity: createCommunityHandler,
				createCommunityPost: createCommunityPostHandler
			});
		},
		[
			createHubItemHandler,
			createCommunityHandler,
			createCommunityPostHandler
		]
	);

	const defaultActions = useMemo(
		(): readonly OverviewAction[] => {
			return getDefaultActions(manageHandler);
		},
		[manageHandler]
	);

	const topBarActions = useMemo(
		(): readonly OverviewAction[] => {
			return buildActionsForTopBar<OverviewActionContext, OverviewAction>(
				{
					mainActions,
					defaultActions
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive,
			mainActions
		]
	);

	const actionContext = useMemo(
		(): OverviewActionContext => {
			const result: OverviewActionContext = {
				canCreateAnnouncement: hubItemKindsCanCreateNewCategoriesFor.includes(HubItemKind.Announcement),
				canCreateEvent: hubItemKindsCanCreateNewCategoriesFor.includes(HubItemKind.Event),
				canCreateKnowledgeItem: hubItemKindsCanCreateNewCategoriesFor.includes(HubItemKind.KnowledgeItem),
				canOpenHubManagement,
				communityCreationPermissions
			};

			for (const category of categories) {
				if (!category.canCreateItem) {
					continue;
				}

				switch (category.hubItemKind) {
					case HubItemKind.Announcement:
						result.canCreateAnnouncement = true;
						break;
					case HubItemKind.Event:
						result.canCreateEvent = true;
						break;
					case HubItemKind.KnowledgeItem:
						result.canCreateKnowledgeItem = true;
						break;
					default:
						never(category.hubItemKind);
				}
			}

			return result;
		},
		[
			canOpenHubManagement,
			categories,
			hubItemKindsCanCreateNewCategoriesFor,
			communityCreationPermissions
		]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				setLoadingState({
					isLoading: true
				});

				try {
					const model: HubOverviewModel = await listItems(signal);

					setLoadingState({
						isLoading: false,
						hasError: false,
						model
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setLoadingState({
						isLoading: false,
						hasError: true
					});
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[loadVersion]
	);

	const renderContent = (loadingState: LoadingState): ReactElement => {
		if (loadingState.isLoading) {
			return (
				<BusyIndicator />
			);
		}

		if (loadingState.hasError) {
			return (
				<div className={styles.noResults}>
					<NoResults
						trackingId="has-error"
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
						onRetry={reload}
						height="fill"
					/>
				</div>
			);
		}

		const { model } = loadingState;

		const isEmpty: boolean = model.announcements
				.length === 0
			&& model.events
				.length === 0
			&& model.knowledgeItems
				.length === 0
			&& (!model.communityPosts
				.isSet
				|| model.communityPosts
					.value
					.length === 0
			);

		if (isEmpty) {
			return (
				<div className={styles.noResults}>
					<NoResults
						trackingId="no-items"
						icon="the-hub"
						title={localize("_NoItemsToShow")}
						message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
						height="fill"
					/>
				</div>
			);
		}

		return (
			<>
				<ul
					className={styles.sections}
					{...setTrackingId("hub-overview")}
				>
					{model.announcements.length > 0 && (
						<li>
							<AnnouncementSection
								items={model.announcements}
								isMobile={isMobile}
								onViewChange={onViewChange}
								onActiveFilterChange={onActiveFilterChange}
								onSelectCategory={onSelectCategory}
								timezone={userTimezone}
							/>
						</li>
					)}

					{model.communityPosts.isSet && model.communityPosts.value.length > 0 && (
						<li>
							<CommunityPostsSection
								items={model.communityPosts.value}
								isMobile={isMobile}
								timezone={userTimezone}
								onViewChange={onViewChange}
							/>
						</li>
					)}

					{model.events.length > 0 && (
						<li>
							<EventSection
								items={model.events}
								isMobile={isMobile}
								onViewChange={onViewChange}
								getCategory={getCategory}
								onActiveFilterChange={onActiveFilterChange}
								onSelectCategory={onSelectCategory}
								timezone={userTimezone}
							/>
						</li>
					)}

					{model.knowledgeItems.length > 0 && (
						<li>
							<KnowledgeItemSection
								items={model.knowledgeItems}
								isMobile={isMobile}
								onViewChange={onViewChange}
								getCategory={getCategory}
								onActiveFilterChange={onActiveFilterChange}
								onSelectCategory={onSelectCategory}
								timezone={userTimezone}
							/>
						</li>
					)}
				</ul>

				<AiAssistantFabSpacing />
				<div className="preserve-space-for-mobile-plus-button" />
			</>
		);
	};

	return (
		<>
			<div className={styles.root}>
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}

				<div className={styles.content}>
					<div className={styles.pageTitle}>
						<PageContentTitle
							title={localize("_Overview")}
							actions={topBarActions}
							actionContext={actionContext}
						/>
					</div>

					{renderContent(loadingState)}
				</div>
			</div>

			<FloatingActionListMenu
				actions={mainActions}
				actionContext={actionContext}
				menuTitle={localize("_Create")}
				trackingId="hub-fab-menu"
			/>

			{createHubItemDialog}
			{hubManagementDialog}
			{editCommunityDialog}
			{createCommunityPostDialog}
		</>
	);
};
Overview.displayName = "Overview";

type LoadingState =
	{
		isLoading: true;
	}
	|
	(
		{
			isLoading: false;
		}
		&
		(
			{
				hasError: true;
			}
			|
			{
				hasError: false;
				model: HubOverviewModel;
			}
		)
	);

type OverviewActionContext = {
	canCreateAnnouncement: boolean;
	canCreateEvent: boolean;
	canCreateKnowledgeItem: boolean;
	canOpenHubManagement: boolean;
	communityCreationPermissions: CommunityCreationPermissions;
};
type OverviewAction = ToolbarAction<OverviewActionContext>;

const newGroup: ToolbarActionGroup = {
	kind: "regular",
	name: "new",
	title: localize("_New"),
	icon: "plus",
	isPrimary: true
};

type MainActionsHandlers = {
	createHubItem: (kind: HubItemKind) => void;
	createCommunity: () => void;
	createCommunityPost: () => void;
};

function getMainActions(
	handlers: MainActionsHandlers
): readonly OverviewAction[] {
	const {
		createHubItem,
		createCommunity,
		createCommunityPost
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "new-announcement",
			title: localize(hubItemKindToLocalizationKey[HubItemKind.Announcement]),
			icon: "bullhorn",
			group: newGroup,
			canExecute: (c) => c.canCreateAnnouncement,
			execute: () => createHubItem(HubItemKind.Announcement)
		},
		...getCommunityCreationActions(
			createCommunity,
			createCommunityPost
		),
		{
			kind: ActionKind.Regular,
			name: "new-event",
			title: localize(hubItemKindToLocalizationKey[HubItemKind.Event]),
			icon: "calendar-star",
			group: newGroup,
			canExecute: (c) => c.canCreateEvent,
			execute: () => createHubItem(HubItemKind.Event)
		},
		{
			kind: ActionKind.Regular,
			name: "new-knowledge-item",
			title: localize(hubItemKindToLocalizationKey[HubItemKind.KnowledgeItem]),
			icon: "book",
			group: newGroup,
			canExecute: (c) => c.canCreateKnowledgeItem,
			execute: () => createHubItem(HubItemKind.KnowledgeItem)
		}
	];
};

function getCommunityCreationActions(
	createCommunity: () => void,
	createCommunityPost: () => void
): readonly OverviewAction[] {
	return [
		{
			kind: ActionKind.Divider,
			name: "community-top-divider",
			group: newGroup,
			canExecute: (c) => {
				const {
					canCreateCommunity,
					canCreateCommunityPost
				} = c.communityCreationPermissions;

				return canCreateCommunity
					&& canCreateCommunityPost;
			},
		},
		{
			kind: ActionKind.Regular,
			name: "new-community",
			title: localize("_Community"),
			icon: "user-group",
			group: newGroup,
			canExecute: (c) => c.communityCreationPermissions
				.canCreateCommunity,
			execute: () => createCommunity()
		},
		{
			kind: ActionKind.Regular,
			name: "new-community-post",
			title: localize("_CommunityPost"),
			icon: "pen-to-square",
			group: newGroup,
			canExecute: (c) => c.communityCreationPermissions
				.canCreateCommunityPost,
			execute: () => createCommunityPost()
		},
		{
			kind: ActionKind.Divider,
			name: "community-bottom-divider",
			group: newGroup,
			canExecute: (c) => {
				const {
					canCreateCommunity,
					canCreateCommunityPost
				} = c.communityCreationPermissions;

				return canCreateCommunity
					&& canCreateCommunityPost;
			},
		}
	];
}

function getDefaultActions(
	manage: () => void
): readonly OverviewAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "manage-item",
			title: localize("_Manage"),
			icon: "gear",
			canExecute: (c) => c.canOpenHubManagement,
			execute: () => manage()
		}
	];
};

export const hubItemKindToLocalizationKey: Record<HubItemKind, LocalizationKey> = {
	[HubItemKind.Announcement]: "_Announcement",
	[HubItemKind.Event]: "_Event",
	[HubItemKind.KnowledgeItem]: "_KnowledgeArticle"
};